import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useProjects } from "../lib/projects";
import { ApiError } from "../lib/api";
import { RuleActionField, RuleConflictNotice } from "../components/RuleAction";
import {
  conditionOnFieldChange,
  isTestContactField,
  operatorsForField,
  projectIdFromSearch,
  ruleConflictMessage,
} from "../lib/rule-action";
import {
  useCreateRule,
  useRule,
  useRulePreview,
  useUpdateRule,
  type Operator,
  type RuleAction,
  type RuleInput,
} from "../lib/rules";

const EVENT_TYPES = [
  "*",
  "PageView",
  "Interaction",
  "Lead",
  "HQLead",
  "WebinarAttendance",
  "Contact",
  "Purchase",
];

const FIELD_SUGGESTIONS = [
  "deal_value",
  "deal_currency",
  "event_type",
  "event_source",
  "lead_country",
  "lead_industry",
  "utm_source",
  "utm_campaign",
  "order_id",
  "test_contact",
];

const OPERATORS: { v: Operator; label: string }[] = [
  { v: ">", label: ">" },
  { v: ">=", label: "≥" },
  { v: "<", label: "<" },
  { v: "<=", label: "≤" },
  { v: "==", label: "=" },
  { v: "!=", label: "≠" },
  { v: "exists", label: "ist gesetzt" },
];

interface BuilderState {
  isGlobal: boolean;
  projectIds: number[]; // ignored when isGlobal
  previewProject: number | null; // for global rules, sample BQ from here
  eventType: string;
  action: RuleAction;
  name: string;
  hasCondition: boolean;
  field: string;
  operator: Operator;
  value: string;
}

const INITIAL: BuilderState = {
  isGlobal: false,
  projectIds: [],
  previewProject: null,
  eventType: "Purchase",
  action: "review",
  name: "",
  hasCondition: true,
  field: "deal_value",
  operator: ">",
  value: "1000",
};

function useDebounced<T>(value: T, delay = 500): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export function RuleBuilderPage() {
  const { id } = useParams();
  const ruleId = id ? parseInt(id, 10) : null;
  const editing = ruleId !== null && !Number.isNaN(ruleId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startProjectId = projectIdFromSearch(searchParams.toString());

  const { data: existing } = useRule(editing ? ruleId : null);
  const projects = useProjects();
  const create = useCreateRule();
  const update = useUpdateRule(ruleId ?? 0);

  const [state, setState] = useState<BuilderState>(INITIAL);

  // Hydrate from existing rule
  useEffect(() => {
    if (existing) {
      setState({
        isGlobal: existing.is_global,
        projectIds: existing.project_ids,
        previewProject: existing.project_ids[0] ?? null,
        eventType: existing.event_type,
        action: existing.action,
        name: existing.name ?? "",
        hasCondition: existing.condition !== null,
        field: existing.condition?.field ?? "deal_value",
        operator: existing.condition?.operator ?? ">",
        value:
          existing.condition?.value !== undefined
            ? String(existing.condition.value)
            : "",
      });
    }
  }, [existing]);

  // Default first project when creating non-global
  useEffect(() => {
    if (
      !editing &&
      !state.isGlobal &&
      state.projectIds.length === 0 &&
      projects.data?.items.length
    ) {
      const first =
        projects.data.items.find((p) => p.id === startProjectId) ??
        projects.data.items[0]!;
      setState((s) => ({
        ...s,
        projectIds: [first.id],
        previewProject: first.id,
      }));
    }
  }, [editing, projects.data, state.isGlobal, state.projectIds.length, startProjectId]);

  // Keep previewProject sane for global mode
  useEffect(() => {
    if (
      state.isGlobal &&
      state.previewProject === null &&
      projects.data?.items.length
    ) {
      setState((s) => ({ ...s, previewProject: projects.data.items[0]!.id }));
    }
  }, [state.isGlobal, state.previewProject, projects.data]);

  const ruleInput = useMemo<RuleInput | null>(() => {
    if (!state.eventType) return null;
    if (!state.isGlobal && state.projectIds.length === 0) return null;
    const condition = state.hasCondition
      ? {
          field: state.field.trim(),
          operator: state.operator,
          ...(state.operator !== "exists" && state.value !== ""
            ? { value: state.value }
            : {}),
        }
      : null;
    return {
      isGlobal: state.isGlobal,
      projectIds: state.isGlobal ? [] : state.projectIds,
      previewProject:
        state.isGlobal && state.previewProject
          ? state.previewProject
          : undefined,
      eventType: state.eventType,
      condition,
      action: state.action,
      name: state.name.trim(),
    };
  }, [state]);

  const debouncedRule = useDebounced(ruleInput, 500);
  const preview = useRulePreview(debouncedRule);

  const canSubmit =
    (state.isGlobal || state.projectIds.length > 0) &&
    state.eventType.length > 0 &&
    (!state.hasCondition ||
      (state.field.length > 0 &&
        (state.operator === "exists" || state.value.length > 0)));

  const submit = () => {
    if (!ruleInput) return;
    const onSuccess = () => {
      toast.success(editing ? "Rule aktualisiert" : "Rule erstellt");
      navigate("/rules");
    };
    // A refused save (409 rule_conflict) is explained in the page, not a toast.
    const onError = (err: unknown) => {
      if (ruleConflictMessage(err) === null) {
        const body = err instanceof ApiError ? (err.body as { message?: unknown } | undefined) : undefined;
        const detail = typeof body?.message === "string" ? body.message : (err as Error).message;
        toast.error(`Speichern fehlgeschlagen: ${detail}`);
      }
    };
    if (editing) update.mutate(ruleInput, { onSuccess, onError });
    else create.mutate(ruleInput, { onSuccess, onError });
  };

  const conflict = ruleConflictMessage(editing ? update.error : create.error);

  const toggleProject = (id: number) => {
    setState((s) => {
      const has = s.projectIds.includes(id);
      const projectIds = has
        ? s.projectIds.filter((x) => x !== id)
        : [...s.projectIds, id];
      return { ...s, projectIds };
    });
  };

  return (
    <div className="px-6 py-8">
      <div className="mx-auto max-w-content space-y-6">
        <header>
          <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
            {editing ? `Rule #${ruleId}` : "Neue Rule"}
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
            {editing ? "Rule bearbeiten" : "Approval-Regel"}
          </h1>
        </header>

        <div className="space-y-5 rounded-md border border-border-primary bg-bg-secondary p-6">
          <Field label="Name (optional)">
            <input
              value={state.name}
              maxLength={120}
              onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
              placeholder="z.B. Testkäufe ablehnen"
              className="h-9 w-full rounded-md border border-border-primary bg-bg-primary px-3 text-sm text-text-primary focus:border-border-focus focus:outline-none"
            />
          </Field>

          <Field label="Was passiert bei einem Treffer?">
            <RuleActionField
              value={state.action}
              onChange={(action) => setState((s) => ({ ...s, action }))}
            />
            <p className="mt-2 text-xs text-text-tertiary">
              Treffen mehrere Regeln, gewinnt: ablehnen vor zulassen vor prüfen.
              Zulassen- und Ablehn-Regeln dürfen sich nicht überschneiden.
            </p>
          </Field>

          <Field label="Gilt für">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={state.isGlobal}
                onChange={(e) =>
                  setState((s) => ({
                    ...s,
                    isGlobal: e.target.checked,
                    projectIds: e.target.checked ? [] : s.projectIds,
                  }))
                }
              />
              <span className="font-medium text-text-primary">
                Alle Projekte (global)
              </span>
              <span className="text-xs text-text-tertiary">
                inklusive zukünftig angelegte
              </span>
            </label>

            {!state.isGlobal && (
              <div className="mt-3 space-y-1.5 rounded-md border border-border-primary bg-bg-primary p-3">
                <p className="mb-1 text-xs text-text-secondary">
                  Mindestens ein Projekt auswählen:
                </p>
                {projects.data?.items.length === 0 && (
                  <p className="text-sm text-text-tertiary">
                    Keine Projekte vorhanden.
                  </p>
                )}
                {projects.data?.items.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-2 rounded-sm px-1 py-0.5 text-sm hover:bg-bg-hover"
                  >
                    <input
                      type="checkbox"
                      checked={state.projectIds.includes(p.id)}
                      onChange={() => toggleProject(p.id)}
                    />
                    <span className="text-text-primary">{p.name}</span>
                    <span className="ml-auto font-mono text-xs text-text-tertiary">
                      Funnel-ID {p.funnelId}
                    </span>
                  </label>
                ))}
                {state.projectIds.length > 1 && (
                  <p className="mt-2 border-t border-border-secondary pt-2 text-xs text-text-secondary">
                    Eine Rule, gilt für {state.projectIds.length} Projekte.
                  </p>
                )}
              </div>
            )}

            {state.isGlobal && (
              <div className="mt-3 space-y-2">
                <p className="rounded-md border border-accent bg-accent-subtle px-3 py-2 text-xs text-text-primary">
                  Diese Rule gilt automatisch für <strong>alle</strong>{" "}
                  bestehenden und zukünftigen Projekte.
                </p>
                <div>
                  <p className="mb-1 text-xs text-text-secondary">
                    Live-Preview Sample-Quelle:
                  </p>
                  <select
                    value={state.previewProject ?? ""}
                    onChange={(e) =>
                      setState((s) => ({
                        ...s,
                        previewProject: e.target.value
                          ? Number(e.target.value)
                          : null,
                      }))
                    }
                    className="h-9 w-full rounded-md border border-border-primary bg-bg-primary px-3 text-sm text-text-primary focus:border-border-focus focus:outline-none"
                  >
                    {projects.data?.items.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </Field>

          <Field label="Event-Typ">
            <input
              list="rule-event-types"
              value={state.eventType}
              onChange={(e) =>
                setState((s) => ({ ...s, eventType: e.target.value }))
              }
              className="h-9 w-full rounded-md border border-border-primary bg-bg-primary px-3 font-mono text-sm text-text-primary focus:border-border-focus focus:outline-none"
            />
            <datalist id="rule-event-types">
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <p className="mt-1 text-xs text-text-tertiary">
              <code>*</code> = alle Event-Typen.
            </p>
          </Field>

          <Field label="Bedingung">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={state.hasCondition}
                onChange={(e) =>
                  setState((s) => ({ ...s, hasCondition: e.target.checked }))
                }
              />
              <span className="text-text-primary">
                Nur Events mit zusätzlicher Bedingung
              </span>
            </label>
            {!state.hasCondition && (
              <p className="mt-1 text-xs text-text-tertiary">
                Ohne Bedingung trifft die Regel <strong>alle</strong> Events
                dieses Typs
                {state.action === "reject"
                  ? " — sie werden abgelehnt."
                  : state.action === "allow"
                    ? " — sie werden ohne Prüfung zugelassen."
                    : " — sie gehen in die Approval-Queue."}
              </p>
            )}

            {state.hasCondition && (
              <div className="mt-3 grid grid-cols-[1fr_120px_1fr] gap-2">
                <input
                  list="rule-field-suggestions"
                  value={state.field}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      field: e.target.value,
                      ...conditionOnFieldChange(s.field, e.target.value),
                    }))
                  }
                  placeholder="Feld (z.B. deal_value)"
                  className="h-9 rounded-md border border-border-primary bg-bg-primary px-3 font-mono text-sm text-text-primary focus:border-border-focus focus:outline-none"
                />
                <datalist id="rule-field-suggestions">
                  {FIELD_SUGGESTIONS.map((f) => (
                    <option key={f} value={f} />
                  ))}
                </datalist>
                <select
                  value={state.operator}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      operator: e.target.value as Operator,
                    }))
                  }
                  className="h-9 rounded-md border border-border-primary bg-bg-primary px-2 font-mono text-sm text-text-primary focus:border-border-focus focus:outline-none"
                >
                  {operatorsForField(state.field, OPERATORS).map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {isTestContactField(state.field) ? (
                  <select
                    value={state.value.trim().toLowerCase() === "false" ? "false" : "true"}
                    onChange={(e) => setState((s) => ({ ...s, value: e.target.value }))}
                    className="h-9 rounded-md border border-border-primary bg-bg-primary px-2 text-sm text-text-primary focus:border-border-focus focus:outline-none"
                  >
                    <option value="true">Ja — ist ein Test-Kontakt</option>
                    <option value="false">Nein — ist kein Test-Kontakt</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    value={state.value}
                    onChange={(e) =>
                      setState((s) => ({ ...s, value: e.target.value }))
                    }
                    disabled={state.operator === "exists"}
                    placeholder="Wert (z.B. 1000)"
                    className="h-9 rounded-md border border-border-primary bg-bg-primary px-3 font-mono text-sm text-text-primary focus:border-border-focus focus:outline-none disabled:opacity-50"
                  />
                )}
              </div>
            )}
            {state.hasCondition && isTestContactField(state.field) && (
              <p className="mt-2 text-xs text-text-tertiary">
                Test-Kontakt: Vor- oder Nachname genau „Test“, oder die E-Mail
                hat „test“ direkt vor dem @ bzw. als „+test“-Zusatz. „Ja“ trifft Test-Kontakte,
                „Nein“ alle anderen.
              </p>
            )}
          </Field>
        </div>

        <LivePreview
          loading={preview.isFetching}
          error={preview.error as Error | null}
          data={preview.data}
        />

        <RuleConflictNotice message={conflict} />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate("/rules")}
            className="rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-hover"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit || create.isPending || update.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-accent px-4 py-2 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-50"
          >
            {(create.isPending || update.isPending) && (
              <Loader2 size={14} className="animate-spin" />
            )}
            {editing ? "Speichern" : "Rule erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="mb-1.5 text-sm font-medium text-text-primary">{label}</p>
      {children}
    </div>
  );
}

function LivePreview({
  loading,
  error,
  data,
}: {
  loading: boolean;
  error: Error | null;
  data: import("../lib/rules").RulePreviewResponse | undefined;
}) {
  let body: React.ReactNode;
  let warn = false;

  if (loading && !data) {
    body = (
      <span className="inline-flex items-center gap-1 text-text-tertiary">
        <Loader2 size={12} className="animate-spin" /> Werte werden geprüft…
      </span>
    );
  } else if (error) {
    body = (
      <span className="text-danger">
        Preview-Fehler: {error.message}
      </span>
    );
    warn = true;
  } else if (data?.unavailable) {
    body = <span className="text-text-tertiary">{data.message}</span>;
  } else if (data) {
    const ratio = data.sampleSize > 0 ? data.matchedCount / data.sampleSize : 0;
    body = (
      <>
        Würde{" "}
        <span className="font-mono font-semibold tabular-nums text-accent-text">
          {data.matchedCount}
        </span>{" "}
        von{" "}
        <span className="font-mono tabular-nums">{data.sampleSize}</span> Events
        der letzten {data.sampledFromDays} Tage triggern
        {data.matchedCount === 0 && data.sampleSize > 0 && (
          <span className="ml-2 text-warning">
            — keine Treffer, Bedingung zu eng?
          </span>
        )}
        {ratio > 0.5 && (
          <span className="ml-2 text-warning">
            — &gt;50%, sehr breit
          </span>
        )}
      </>
    );
    warn = data.matchedCount === 0 || ratio > 0.5;
  } else {
    body = (
      <span className="text-text-tertiary">
        Live-Preview erscheint, sobald Projekt + Bedingung vollständig sind.
      </span>
    );
  }

  return (
    <div
      className={`rounded-md border-l-4 p-4 text-sm ${
        warn
          ? "border-warning bg-warning-bg/40"
          : "border-accent bg-accent-subtle"
      }`}
    >
      <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
        Live-Preview
      </p>
      <p className="mt-1 text-text-primary">{body}</p>
      {data && !data.unavailable && data.samples.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-text-secondary">
            {data.samples.length} Beispiel-Treffer
          </summary>
          <ul className="mt-2 space-y-1 text-xs">
            {data.samples.map((s, i) => (
              <li key={i} className="font-mono text-text-secondary">
                {s.event_type} · {s.summary}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
